// Libs
import create from "zustand";

// Local
import { logger } from "../logging";
import { ExistingStiltPhoneCall } from "../models/calls";
import { DbWrite } from "../database";

interface IncomingCallsStore {
  /**
   * A way to mark a particular call Firestore document as the current call. This
   * is only tracked locally on the client and not persisted to the database.
   */
  currentCall: ExistingStiltPhoneCall | null;
  /**
   * Set the current call locally.
   */
  setCurrentCall: (call: ExistingStiltPhoneCall | null) => void;
  /**
   * Clear the current call locally.
   */
  clearCall: () => void;
  /**
   * A convenience method that wraps the more general database update function.
   * Updates the customerID and agentID of the current call ID in the database.
   */
  updateCallCustomer: (customerID: string) => Promise<void>;
  /** A history of customer IDs and their timestamps. */
  customerContextHistory: Array<{ customerID: string; isoTimestamp: string }>;
  /** Add a customer ID to the context history (if there is an incoming/current call) */
  addCustomerContext: (customerID: string) => void;
  clearCustomerContext: () => void;
}

const MAX_CUSTOMER_CONTEXT_HISTORY = 5;

// Export a hook to access data globally and define initial values.
export const useIncomingCallsStore = create<IncomingCallsStore>((set, get) => {
  return {
    currentCall: null,
    customerContextHistory: [],
    addCustomerContext: (customerID) => {
      set((state) => {
        // do not add an entry if there is no current call
        if (!state.currentCall) {
          return { customerContextHistory: state.customerContextHistory };
        }

        const newEntry = { customerID, isoTimestamp: new Date().toISOString() };
        const lastEntry = state.customerContextHistory[0];
        if (lastEntry?.customerID === customerID) {
          return { customerContextHistory: state.customerContextHistory };
        }

        const newHistory = [newEntry, ...state.customerContextHistory];

        if (newHistory.length > MAX_CUSTOMER_CONTEXT_HISTORY) {
          newHistory.length = MAX_CUSTOMER_CONTEXT_HISTORY;
        }
        logger.info("setting customer context history", newHistory);
        return { customerContextHistory: newHistory };
      });
    },
    clearCustomerContext: () => {
      logger.info("clearing customer context");
      set({ customerContextHistory: [] });
    },
    setCurrentCall: (call) => {
      logger.info("setting incoming call", call);
      set({ currentCall: call });
    },
    clearCall: () => {
      logger.info("clearing incoming call");
      set({ currentCall: null });
      get().clearCustomerContext();
    },
    updateCallCustomer: async (customerID) => {
      const call = get().currentCall;
      if (!call) {
        throw new Error(
          `incoming-calls.ts: Expected call to exist. Unable to update customer ${customerID}`,
        );
      }

      const currentCallID = call.id;
      const callRefPath = call.refPath;
      const siteKey = callRefPath.split("/")[1];

      await DbWrite.calls.updateCustomerAndAgent({
        callDocID: currentCallID,
        siteKey: siteKey,
        customerID: customerID,
      });

      get().clearCustomerContext();
      logger.info(
        `updated call doc (${currentCallID}) with customer snapshot and agentID`,
      );
    },
  };
});
